import { useState, useEffect, useRef, useCallback } from "react";
import api from "../../services/api";
import ProtectedLink from "../../components/ProtectedLink";

const MONTHS     = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
const MONTH_ABBR = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
const CATEGORIES = ['produit','charge','investissement','financement'];
const CAT_COLORS = { produit: "#16a34a", charge: "#dc2626", investissement: "#3b82f6", financement: "#8b5cf6" };

function r2(n) { return Math.round((n || 0) * 100) / 100; }
function rowTotal(line) { return r2(MONTHS.reduce((s, m) => s + (parseFloat(line[m]) || 0), 0)); }

const inputS = { width: "100%", border: "none", background: "transparent", textAlign: "right", fontSize: 12, padding: "3px 4px", outline: "none", color: "var(--text,#1f2937)" };

export default function BudgetEntryTab({ scenarioId, scenario, onRefresh, currentCompanyId }) {
  const [lines, setLines]         = useState([]);
  const [editing, setEditing]     = useState(null); // { lineId, month }
  const [editVal, setEditVal]     = useState("");
  const [dirty, setDirty]         = useState({});   // { lineId: { month: value } }
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [showAddLine, setShowAddLine] = useState(false);
  const [newLine, setNewLine]     = useState({ account_id: "", account_name: "", category: "charge", subcategory: "" });
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [showDistrib, setShowDistrib] = useState(false);
  const [distribTarget, setDistribTarget] = useState(null);
  const [distribAmount, setDistribAmount] = useState("");
  const [distribMode, setDistribMode] = useState("equal");
  const autosaveTimer = useRef(null);

  const load = useCallback(() => {
    if (!scenarioId) return;
    api.budget.lines(scenarioId).then(rows => {
      setLines(rows);
      setDirty({});
    }).catch(console.error);
  }, [scenarioId]);

  useEffect(() => { load(); }, [load]);

  // Autosave every 30s
  useEffect(() => {
    if (autosaveTimer.current) clearInterval(autosaveTimer.current);
    autosaveTimer.current = setInterval(() => {
      if (Object.keys(dirty).length > 0) saveBatch(false);
    }, 30000);
    return () => clearInterval(autosaveTimer.current);
  }, [dirty]);

  const getVal = (line, month) => {
    const d = dirty[line.id];
    if (d && d[month] !== undefined) return d[month];
    return line[month] || 0;
  };

  const commitEdit = async () => {
    if (!editing) return;
    const { lineId, month } = editing;
    const parsed = parseFloat(editVal) || 0;
    setDirty(prev => ({ ...prev, [lineId]: { ...(prev[lineId] || {}), [month]: parsed } }));
    setEditing(null);
  };

  const saveBatch = async (showMsg = true) => {
    if (Object.keys(dirty).length === 0 && showMsg) return setMsg("Aucune modification à enregistrer");
    setSaving(true);
    const payload = lines.map(l => {
      const changes = dirty[l.id] || {};
      const merged  = { ...l, ...changes };
      return { ...merged, notes: l.notes };
    }).filter(l => Object.keys(dirty[l.id] || {}).length > 0 || showMsg);

    // Build full batch from all lines that have dirty entries
    const batchLines = lines.map(l => {
      const changes = dirty[l.id] || {};
      return { id: l.id, ...MONTHS.reduce((acc, m) => ({ ...acc, [m]: changes[m] !== undefined ? changes[m] : (l[m] || 0) }), {}), notes: l.notes };
    });

    try {
      await api.budget.batchSave(scenarioId, { lines: batchLines });
      if (showMsg) setMsg("✓ Budget enregistré");
      setDirty({});
      load();
    } catch (e) { setMsg("Erreur : " + e.message); }
    finally { setSaving(false); if (showMsg) setTimeout(() => setMsg(""), 3000); }
  };

  const copyPriorYear = async () => {
    const pct = prompt("Taux de croissance par rapport à N-1 (%) :", "0");
    if (pct === null) return;
    try {
      const r = await api.budget.copyPriorYear(scenarioId, { growth_percent: parseFloat(pct) || 0, company_id: currentCompanyId });
      setMsg("✓ " + r.message);
      load();
    } catch (e) { setMsg("Erreur : " + e.message); }
  };

  const handleDistrib = () => {
    if (!distribTarget) return;
    const total = parseFloat(distribAmount) || 0;
    const line = lines.find(l => l.id === distribTarget);
    if (!line) return;

    let monthVals;
    if (distribMode === "equal") {
      const share = r2(total / 12);
      monthVals = Object.fromEntries(MONTHS.map(m => [m, share]));
    } else {
      // Saisonnalisé (simple: Q1+Q2=30%, Q3=40%, Q4=30%)
      const weights = [1,1,1,1,1,1,1.5,1.5,1.5,1,1,1];
      const wSum = weights.reduce((s, w) => s + w, 0);
      monthVals = Object.fromEntries(MONTHS.map((m, i) => [m, r2(total * weights[i] / wSum)]));
    }
    setDirty(prev => ({ ...prev, [distribTarget]: { ...(prev[distribTarget] || {}), ...monthVals } }));
    setShowDistrib(false);
    setDistribAmount("");
    setDistribTarget(null);
  };

  const handleImport = async (dryRun = true) => {
    if (!importFile) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const b64 = btoa(String.fromCharCode(...new Uint8Array(ev.target.result)));
      try {
        const r = await api.budget.importExcel(scenarioId, { file_base64: b64, dry_run: dryRun });
        if (dryRun) { setImportPreview(r.preview); }
        else { setImportPreview(null); setShowImport(false); setImportFile(null); setMsg("✓ " + r.imported + " ligne(s) importée(s)"); load(); }
      } catch (e) { setMsg("Erreur import : " + e.message); }
    };
    reader.readAsArrayBuffer(importFile);
  };

  const addLine = async () => {
    if (!newLine.account_id || !newLine.account_name) return;
    try {
      await api.budget.addCustomLine(scenarioId, newLine);
      setNewLine({ account_id: "", account_name: "", category: "charge", subcategory: "" });
      setShowAddLine(false);
      load();
    } catch (e) { setMsg("Erreur : " + e.message); }
  };

  const generateVariants = async () => {
    if (!window.confirm("Générer automatiquement 3 scénarios (Optimiste +20%, Pessimiste -20%, Crise point mort) depuis ce scénario central ?")) return;
    setGenLoading(true);
    try {
      const r = await api.budget.generateVariants(scenarioId, { company_id: currentCompanyId });
      setMsg(`✓ ${r.message}`);
      if (onRefresh) onRefresh();
    } catch (e) { setMsg("Erreur : " + e.message); }
    finally { setGenLoading(false); setTimeout(() => setMsg(""), 4000); }
  };

  const validate = async () => {
    if (!window.confirm("Valider le budget ? Cette action finalise le scénario.")) return;
    try {
      await api.budget.validateScenario(scenarioId);
      setMsg("✓ Budget validé");
      if (onRefresh) onRefresh();
    } catch (e) { setMsg("Erreur : " + e.message); }
  };

  // Group lines by category
  const byCategory = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = lines.filter(l => l.category === cat);
    return acc;
  }, {});

  const categoryTotal = (cat, month) => r2(
    (byCategory[cat] || []).reduce((s, l) => s + (dirty[l.id]?.[month] !== undefined ? (dirty[l.id][month] || 0) : (l[month] || 0)), 0)
  );

  const produitMonthly = (m) => categoryTotal('produit', m);
  const chargeMonthly  = (m) => categoryTotal('charge', m);
  const resultMonthly  = (m) => r2(produitMonthly(m) - chargeMonthly(m));

  const cellStyle = (active) => ({
    padding: "4px 2px", borderRight: "1px solid #f3f4f6",
    background: active ? "#eff6ff" : "transparent",
    cursor: "text",
    minWidth: 72,
  });

  if (!scenarioId) return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)", fontSize: 13 }}>Sélectionnez un scénario</div>;

  const isValidated = scenario?.status === 'validé';

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <button onClick={copyPriorYear} disabled={isValidated} style={{ padding: "7px 13px", background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, cursor: "pointer" }}>
          📋 Copier N-1
        </button>
        <button onClick={() => setShowDistrib(true)} disabled={isValidated} style={{ padding: "7px 13px", background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, cursor: "pointer" }}>
          ÷ Répartir annuel
        </button>
        <button onClick={() => setShowImport(true)} disabled={isValidated} style={{ padding: "7px 13px", background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, cursor: "pointer" }}>
          ⬆ Import Excel
        </button>
        <ProtectedLink url={api.budget.templateUrl()} style={{ padding: "7px 13px", background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, textDecoration: "none", color: "var(--text,#1f2937)" }}>
          ⬇ Template
        </ProtectedLink>
        <button onClick={() => setShowAddLine(true)} disabled={isValidated} style={{ padding: "7px 13px", background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, cursor: "pointer" }}>
          + Ajouter ligne
        </button>
        <button onClick={generateVariants} disabled={isValidated || genLoading} style={{ padding: "7px 13px", background: "#8b5cf622", color: "#8b5cf6", border: "1px solid #8b5cf644", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          {genLoading ? "Génération..." : "🔀 Générer scénarios"}
        </button>
        <div style={{ flex: 1 }} />
        {msg && <span style={{ fontSize: 12, color: msg.startsWith("✓") ? "#16a34a" : "#dc2626" }}>{msg}</span>}
        <button onClick={() => saveBatch(true)} disabled={saving || isValidated} style={{ padding: "7px 14px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          {saving ? "Enreg..." : "💾 Enregistrer"}
        </button>
        {!isValidated && (
          <button onClick={validate} style={{ padding: "7px 14px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            ✓ Valider le budget
          </button>
        )}
        {isValidated && <span style={{ fontSize: 12, color: "#16a34a", fontWeight: 600 }}>✓ Budget validé — lecture seule</span>}
      </div>

      {/* Spreadsheet */}
      <div style={{ overflowX: "auto", background: "var(--card-bg,#fff)", border: "1px solid var(--border-color,#e2e8f0)", borderRadius: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#f9fafb", position: "sticky", top: 0, zIndex: 10 }}>
              <th style={{ padding: "8px 12px", textAlign: "left", minWidth: 60, borderRight: "1px solid #e5e7eb", fontWeight: 600, color: "#6b7280", fontSize: 11 }}>Compte</th>
              <th style={{ padding: "8px 12px", textAlign: "left", minWidth: 180, borderRight: "1px solid #e5e7eb", fontWeight: 600, color: "#6b7280", fontSize: 11 }}>Désignation</th>
              {MONTH_ABBR.map((m, i) => (
                <th key={i} style={{ padding: "8px 4px", textAlign: "right", minWidth: 72, borderRight: "1px solid #e5e7eb", fontWeight: 600, color: "#6b7280", fontSize: 11 }}>{m}</th>
              ))}
              <th style={{ padding: "8px 8px", textAlign: "right", minWidth: 80, fontWeight: 700, color: "#1f2937", fontSize: 11, background: "#f0f0f0" }}>Total</th>
              {!isValidated && <th style={{ padding: "8px 6px", width: 28 }} />}
            </tr>
          </thead>
          <tbody>
            {CATEGORIES.map(cat => {
              if (!byCategory[cat] || byCategory[cat].length === 0) return null;
              const catTotal = rowTotal({ ...MONTHS.reduce((acc, m) => ({ ...acc, [m]: categoryTotal(cat, m) }), {}) });
              return [
                // Category header
                <tr key={`cat-${cat}`} style={{ background: `${CAT_COLORS[cat]}10`, borderTop: "2px solid #e5e7eb" }}>
                  <td colSpan={2} style={{ padding: "7px 12px", fontWeight: 700, color: CAT_COLORS[cat], fontSize: 12, borderRight: "1px solid #e5e7eb" }}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}s
                  </td>
                  {MONTHS.map((m, i) => (
                    <td key={i} style={{ padding: "7px 4px", textAlign: "right", fontWeight: 700, color: CAT_COLORS[cat], borderRight: "1px solid #e5e7eb", fontSize: 11 }}>
                      {categoryTotal(cat, m).toLocaleString("fr-FR", { maximumFractionDigits: 0 })}
                    </td>
                  ))}
                  <td style={{ padding: "7px 8px", textAlign: "right", fontWeight: 700, color: CAT_COLORS[cat], background: "#f0f0f0" }}>
                    {catTotal.toLocaleString("fr-FR", { maximumFractionDigits: 0 })}
                  </td>
                  {!isValidated && <td />}
                </tr>,
                // Lines
                ...byCategory[cat].map(line => {
                  const lineTotal = r2(MONTHS.reduce((s, m) => s + (getVal(line, m) || 0), 0));
                  const isDirtyLine = !!dirty[line.id];
                  return (
                    <tr key={line.id} style={{ borderBottom: "1px solid #f9fafb", background: isDirtyLine ? "#fffbeb" : "transparent" }}>
                      <td style={{ padding: "4px 12px", color: "#6b7280", fontSize: 11, borderRight: "1px solid #f3f4f6" }}>{line.account_id}</td>
                      <td style={{ padding: "4px 8px", maxWidth: 200, borderRight: "1px solid #f3f4f6" }}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{line.account_name}</div>
                        {line.subcategory && <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{line.subcategory}</div>}
                      </td>
                      {MONTHS.map((m, i) => {
                        const isActive = editing?.lineId === line.id && editing?.month === m;
                        const val      = getVal(line, m);
                        return (
                          <td key={i} style={cellStyle(isActive)} onClick={() => {
                            if (isValidated) return;
                            if (editing) commitEdit();
                            setEditing({ lineId: line.id, month: m });
                            setEditVal(String(val || ""));
                          }}>
                            {isActive
                              ? <input autoFocus style={inputS} value={editVal} onChange={e => setEditVal(e.target.value)}
                                  onBlur={commitEdit} onKeyDown={e => { if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); commitEdit(); } }}
                                  type="number" step="1" />
                              : <div style={{ textAlign: "right", padding: "3px 4px", color: val > 0 ? "var(--text,#1f2937)" : "#d1d5db" }}>
                                  {val > 0 ? val.toLocaleString("fr-FR", { maximumFractionDigits: 0 }) : "—"}
                                </div>}
                          </td>
                        );
                      })}
                      <td style={{ padding: "4px 8px", textAlign: "right", fontWeight: 600, background: "#f9fafb" }}>
                        {lineTotal.toLocaleString("fr-FR", { maximumFractionDigits: 0 })}
                      </td>
                      {!isValidated && (
                        <td style={{ padding: "2px 4px" }}>
                          <button onClick={() => { setDistribTarget(line.id); setShowDistrib(true); }} title="Répartir" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "#9ca3af" }}>÷</button>
                        </td>
                      )}
                    </tr>
                  );
                }),
              ];
            })}

            {/* Résultat row */}
            <tr style={{ background: "#1a9e7215", borderTop: "3px solid #1a9e72" }}>
              <td colSpan={2} style={{ padding: "8px 12px", fontWeight: 700, color: "#1a9e72", fontSize: 13, borderRight: "1px solid #e5e7eb" }}>
                RÉSULTAT (Produits − Charges)
              </td>
              {MONTHS.map((m, i) => {
                const res = resultMonthly(m);
                return (
                  <td key={i} style={{ padding: "8px 4px", textAlign: "right", fontWeight: 700, color: res >= 0 ? "#16a34a" : "#dc2626", borderRight: "1px solid #e5e7eb", fontSize: 12 }}>
                    {res.toLocaleString("fr-FR", { maximumFractionDigits: 0 })}
                  </td>
                );
              })}
              <td style={{ padding: "8px 8px", textAlign: "right", fontWeight: 700, background: "#e6f9f3", color: "#1a9e72", fontSize: 13 }}>
                {r2(MONTHS.reduce((s, m) => s + resultMonthly(m), 0)).toLocaleString("fr-FR", { maximumFractionDigits: 0 })}
              </td>
              {!isValidated && <td />}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Répartir modal */}
      {showDistrib && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "var(--card-bg,#fff)", borderRadius: 12, padding: 24, width: 380 }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: 15, fontWeight: 700 }}>Répartir montant annuel</h3>
            {distribTarget && (
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
                Ligne : {lines.find(l => l.id === distribTarget)?.account_name}
              </div>
            )}
            {!distribTarget && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Ligne</label>
                <select style={{ width: "100%", padding: "7px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, background: "#fff" }}
                  value={distribTarget || ""} onChange={e => setDistribTarget(parseInt(e.target.value))}>
                  <option value="">Sélectionner...</option>
                  {lines.map(l => <option key={l.id} value={l.id}>{l.account_id} — {l.account_name}</option>)}
                </select>
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Montant annuel (€)</label>
              <input type="number" step="1" value={distribAmount} onChange={e => setDistribAmount(e.target.value)}
                style={{ width: "100%", padding: "7px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, display: "block", marginBottom: 6 }}>Mode de répartition</label>
              {[["equal","Égale (÷12)"],["seasonal","Saisonnalisée (Été +50%)"]].map(([v, l]) => (
                <label key={v} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, marginBottom: 6, cursor: "pointer" }}>
                  <input type="radio" name="distrib" value={v} checked={distribMode === v} onChange={() => setDistribMode(v)} />
                  {l}
                </label>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => { setShowDistrib(false); setDistribTarget(null); }} style={{ padding: "7px 14px", border: "1px solid #e5e7eb", borderRadius: 8, background: "transparent", cursor: "pointer", fontSize: 12 }}>Annuler</button>
              <button onClick={handleDistrib} style={{ padding: "7px 14px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Appliquer</button>
            </div>
          </div>
        </div>
      )}

      {/* Import Excel modal */}
      {showImport && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "var(--card-bg,#fff)", borderRadius: 12, padding: 24, width: 540 }}>
            <h3 style={{ margin: "0 0 14px 0", fontSize: 15, fontWeight: 700 }}>Import Excel</h3>
            <input type="file" accept=".xlsx,.xls" onChange={e => { setImportFile(e.target.files[0]); setImportPreview(null); }} style={{ marginBottom: 12 }} />
            {!importPreview ? (
              <button onClick={() => handleImport(true)} disabled={!importFile} style={{ padding: "7px 14px", background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, cursor: "pointer", marginRight: 8 }}>
                Prévisualiser
              </button>
            ) : (
              <div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>{importPreview.length} lignes détectées (aperçu) :</div>
                <div style={{ maxHeight: 200, overflowY: "auto", fontSize: 11, background: "#f9fafb", borderRadius: 6, padding: 10 }}>
                  {importPreview.map((r, i) => <div key={i}>{r.account_id} — {r.account_name} ({r.category})</div>)}
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
              <button onClick={() => { setShowImport(false); setImportFile(null); setImportPreview(null); }} style={{ padding: "7px 14px", border: "1px solid #e5e7eb", borderRadius: 8, background: "transparent", cursor: "pointer", fontSize: 12 }}>Annuler</button>
              {importPreview && (
                <button onClick={() => handleImport(false)} style={{ padding: "7px 14px", background: "#1a9e72", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                  Importer ({importPreview.length} lignes)
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add line modal */}
      {showAddLine && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "var(--card-bg,#fff)", borderRadius: 12, padding: 24, width: 420 }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: 15, fontWeight: 700 }}>Ajouter une ligne</h3>
            {[
              { label: "Compte PCG", key: "account_id", placeholder: "641000" },
              { label: "Désignation", key: "account_name", placeholder: "Salaires" },
              { label: "Sous-catégorie", key: "subcategory", placeholder: "Charges personnel" },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, marginBottom: 4, color: "var(--text-muted)" }}>{f.label}</label>
                <input value={newLine[f.key]} onChange={e => setNewLine(prev => ({ ...prev, [f.key]: e.target.value }))}
                  placeholder={f.placeholder} style={{ width: "100%", padding: "7px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }} />
              </div>
            ))}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 12, marginBottom: 4, color: "var(--text-muted)" }}>Catégorie</label>
              <select value={newLine.category} onChange={e => setNewLine(p => ({ ...p, category: e.target.value }))}
                style={{ width: "100%", padding: "7px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, background: "#fff" }}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setShowAddLine(false)} style={{ padding: "7px 14px", border: "1px solid #e5e7eb", borderRadius: 8, background: "transparent", cursor: "pointer", fontSize: 12 }}>Annuler</button>
              <button onClick={addLine} style={{ padding: "7px 14px", background: "#1a9e72", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Ajouter</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
